// Some browsers carry keyboard :focus-visible modality into element.focus(). That is useful for
// the focus ring, but it is not proof that the user explicitly navigated to the target. Modal uses
// this synchronous marker for lifecycle-driven focus so transient UI can distinguish that event
// without removing focus, the ring, or the target's place in the Tab order.
const lifecycleFocusTargets = new WeakSet<HTMLElement>();

/** @internal Move focus as part of a component lifecycle while preserving browser focus styling. */
export function focusForLifecycle(target: HTMLElement | null | undefined): void {
  if (!target) return;

  const alreadyMarked = lifecycleFocusTargets.has(target);
  if (!alreadyMarked) lifecycleFocusTargets.add(target);
  try {
    target.focus();
  } finally {
    if (!alreadyMarked) lifecycleFocusTargets.delete(target);
  }
}

// Consumer autoFocus inside a mounting modal dispatches its focus during React's commit, before any
// Modal effect can mark the target element — so the mount commit is covered per portal root instead:
// opened right before the portal renders, closed in the Modal's layout effect once children (and
// their autoFocus) have mounted.
const mountWindowRoots = new Set<HTMLElement>();

/** @internal Treat focus landing inside `root` as lifecycle focus until the window closes. */
export function openLifecycleFocusWindow(root: HTMLElement): void {
  mountWindowRoots.add(root);
}

/** @internal Close the mount window for `root`. Safe to call more than once. */
export function closeLifecycleFocusWindow(root: HTMLElement): void {
  mountWindowRoots.delete(root);
}

/** @internal True only while a focus event dispatched by `focusForLifecycle` is being handled,
 * or while the target sits inside a portal root whose mount window is still open. */
export const isLifecycleFocus = (target: HTMLElement): boolean => {
  if (lifecycleFocusTargets.has(target)) return true;
  for (const root of mountWindowRoots) if (root.contains(target)) return true;
  return false;
};
